import React, { useEffect, useMemo } from "react";
import { Input, Select, SecondaryButton, SearchIcon, XIcon } from "../../../design-system";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

const MessageFilters = ({ filters, setFilters, userContainers, messages }) => {
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const containers = useMemo(() => {
    return Array.isArray(userContainers) ? userContainers : [];
  }, [userContainers]);

  const contactMessages = useMemo(() => {
    return Array.isArray(messages) ? messages.filter((msg) => msg?.kind === "contact") : [];
  }, [messages]);

  const websiteContainers = useMemo(() => {
    return containers.filter((container) => container?.id != null);
  }, [containers]);

  const showWebsiteFilter = websiteContainers.length > 0 && contactMessages.length > 0;

  const websiteOptions = useMemo(() => {
    return [
      { value: "", label: "All websites" },
      ...websiteContainers.map((container) => ({
        value: String(container.id),
        label: container.name || container.domain || `Website ${container.id}`,
      })),
    ];
  }, [websiteContainers]);

  // Avoid "hidden filters" affecting results.
  useEffect(() => {
    if (!showWebsiteFilter && filters.website) {
      setFilters((prev) => ({ ...prev, website: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWebsiteFilter]);

  const clearFilters = () => {
    setFilters({
      search: "",
      website: "",
      unreadOnly: false,
    });
  };

  const hasActiveFilters =
    filters.search || (showWebsiteFilter && filters.website) || filters.unreadOnly;

  return (
    <div className={`p-4 ${surfaces.primary} border-b ${borders.default}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
        {/* Search */}
        <div className="w-full sm:flex-1 min-w-0 sm:min-w-64">
          <Input
            type="text"
            placeholder="Search by sender name, email, or message content..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            leftIcon={<SearchIcon size="sm" />}
          />
        </div>

        {/* Website Filter */}
        {showWebsiteFilter && (
          <div className="w-full sm:w-48">
            <Select
              value={filters.website}
              onChange={(e) => handleFilterChange('website', e.target.value)}
              options={websiteOptions}
              placeholder=""
            />
          </div>
        )}

        {/* Unread Only Toggle */}
        <div className="w-full sm:w-auto flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={filters.unreadOnly}
              onChange={(e) => handleFilterChange('unreadOnly', e.target.checked)}
              className="rounded border-gray-300 dark:border-dark-600 text-blue-600 dark:text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            <span className={`ml-2 ${typography.bodySmall} ${textColors.primary}`}>Unread only</span>
          </label>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <SecondaryButton
            onClick={clearFilters}
            size="sm"
            className="w-full sm:w-auto whitespace-nowrap"
          >
            Clear filters
          </SecondaryButton>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={`${typography.label} ${textColors.secondary}`}>Active filters:</span>
          
          {filters.search && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${typography.caption} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 min-w-0 max-w-full`}
              title={`Search: "${filters.search}"`}
            >
              <span className="min-w-0 truncate">Search: "{filters.search}"</span>
              <button
                onClick={() => handleFilterChange('search', '')}
                className="ml-1 inline-flex items-center justify-center w-4 h-4 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                aria-label="Clear search filter"
              >
                <XIcon size="xs" />
              </button>
            </span>
          )}
          
          {showWebsiteFilter && filters.website && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${typography.caption} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 min-w-0 max-w-full`}
              title={`Website: ${websiteContainers.find(c => c.id.toString() === filters.website)?.name || ""}`}
            >
              <span className="min-w-0 truncate">
                Website: {websiteContainers.find(c => c.id.toString() === filters.website)?.name}
              </span>
              <button
                onClick={() => handleFilterChange('website', '')}
                className="ml-1 inline-flex items-center justify-center w-4 h-4 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                aria-label="Clear website filter"
              >
                <XIcon size="xs" />
              </button>
            </span>
          )}
          
          {filters.unreadOnly && (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${typography.caption} bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300`}>
              Unread only
              <button
                onClick={() => handleFilterChange('unreadOnly', false)}
                className="ml-1 inline-flex items-center justify-center w-4 h-4 text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200"
                aria-label="Clear unread filter"
              >
                <XIcon size="xs" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageFilters; 